import { Box, Chip, Grid2, Typography, Stack, IconButton, LinearProgress } from '@mui/material';
import React, { useState } from 'react';
import { uploadFile } from '@openapi/files/files';
import { STATIC_CDN_URL } from '../../../config';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';

const getImageUrl = (val) => {
    if (!val) return null;
    if (val.startsWith('http://') || val.startsWith('https://')) {
        return val;
    }
    return `${STATIC_CDN_URL}${val.startsWith('/') ? '' : '/'}${val}`;
};

const UploadImageInput = ({ multiple = false, value, setFieldValue, title }) => {
    const { t } = useTranslation();
    const [uploadProcessing, setUploadProcessing] = useState(false);

    // If multiple, value is an array; else, it's a string
    const images = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

    const handleRemove = (idx) => {
        if (multiple) {
            const newImages = images.filter((_, i) => i !== idx);
            setFieldValue(newImages);
        } else {
            setFieldValue(null);
        }
    };

    const handleChange = async (event) => {
        if (!event.target.files || !event.target.files.length) {
            return;
        }
        const files = Array.from(event.target.files);
        if (multiple) {
            const uploaded = [];
            let idx = 0;
            for (const f of files) {
                idx++;
                setUploadProcessing(idx / files.length);
                const response = await uploadFile({ file: f });
                uploaded.push(response.url);
            }
            setUploadProcessing(false);
            setFieldValue([...images, ...uploaded]);
        } else {
            const f = files[0];
            setUploadProcessing(true);
            const response = await uploadFile({ file: f });
            setFieldValue(response.url);
            setUploadProcessing(false);
        }
    };

    return (
        <Grid2 size={{ xs: 12 }} sx={{ mb: 2 }}>
            <Stack alignItems="flex-start" spacing={1}>
                <Typography variant="body1">{t(title)}</Typography>
                <Box
                    sx={{
                        width: '100%',
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#fafafa',
                    }}
                >
                    <Stack
                        spacing={2}
                        direction="row"
                        useFlexGap
                        sx={{ flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}
                    >
                        {images.length > 0 &&
                            images.map((img, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        minWidth: 50,
                                        minHeight: 50,
                                        backgroundColor: '#f0f0f0',
                                    }}
                                >
                                    <img
                                        src={getImageUrl(img)}
                                        alt=""
                                        style={{
                                            maxWidth: '160px',
                                            maxHeight: '160px',
                                            display: 'block',
                                            borderRadius: 4,
                                            marginBottom: 4,
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            '&:hover': { backgroundColor: 'primary.dark' },
                                        }}
                                        onClick={() => handleRemove(idx)}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                    </Stack>
                    {uploadProcessing !== false && (
                        <Box sx={{ width: '100%', flexDirection: 'column', alignItems: 'center' }}>
                            <LinearProgress
                                variant={
                                    uploadProcessing === true ? 'indeterminate' : 'determinate'
                                }
                                height={10}
                                value={uploadProcessing * 100}
                            />
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    mt: 1,
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                }}
                            >
                                {t('image.uploading')}
                            </Box>
                            {uploadProcessing !== true && (
                                <Box sx={{ textAlign: 'center', mt: 1 }}>
                                    {Math.floor(uploadProcessing * 100)}%
                                </Box>
                            )}
                        </Box>
                    )}
                    {(multiple || !value) && uploadProcessing === false && (
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{ flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}
                        >
                            <Box
                                sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                            >
                                <input
                                    style={{
                                        opacity: 0,
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        cursor: 'pointer',
                                    }}
                                    type="file"
                                    accept="image/*"
                                    multiple={multiple}
                                    onChange={handleChange}
                                />
                                <Chip
                                    label={t('image.upload')}
                                    color="primary"
                                    sx={{ cursor: 'pointer' }}
                                />
                            </Box>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Grid2>
    );
};

export default UploadImageInput;
